import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { refreshWhenIdle } from "../../utils/turbo-refresh.util"

/**
 * Stimulus controller for managing Tatra Bank connection UI
 * Handles connection, disconnection, and sync operations with visual feedback
 */
export default class extends Controller {
  static targets = ["connectionCard", "syncCard", "connectButton", "disconnectButton", "syncButton", "syncFullButton"]

  static values = {
    workspaceId: String,
    connectionStatusUrl: String,
    syncStartedMsg: String,
    syncFailedMsg: String,
    syncErrorMsg: String
  }

  connect() {
    this.reloadTimer = null
  }

  disconnect() {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer)
      this.reloadTimer = null
    }
    this.cancelReload?.()
  }

  /**
   * Handle connect button click
   * Shows loading state and redirects to bank authorization
   */
  connectBank(event) {
    const button = event.currentTarget

    // Show loading state visually without disabling (disabled prevents form submit)
    button.dataset.originalHtml = button.innerHTML
    const icon = button.querySelector(".material-symbols-outlined")
    if (icon) {
      icon.textContent = "sync"
      icon.classList.add("spin-animation")
    }

    // Let the form submit naturally - don't disable the button
    // The form has data-turbo="false" so it will do a full-page POST
    // which redirects to the bank's OAuth authorization page
  }

  /**
   * Handle sync operations
   * Shows loading state and disables buttons during sync
   */
  sync(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.performSync(button)
  }

  syncFull(event) {
    event.preventDefault()
    this.performSync(event.currentTarget)
  }

  /**
   * Perform sync operation with loading state
   */
  async performSync(button) {
    const form = button.closest("form")
    if (!form) return

    // Disable all sync buttons
    this.disableAllSyncButtons()

    // Set loading state on clicked button
    this.setButtonLoading(button, true)

    try {
      const response = await post(form.action, {
        body: new FormData(form),
        responseKind: "json"
      })

      if (response.ok) {
        this.showToast("success", this.syncStartedMsgValue || "Sync started")

        // Poll for sync completion before reloading
        this.#pollSyncStatus()
      } else {
        const errorData = await response.json.catch(() => ({}))
        this.showToast("error", errorData.error || this.syncFailedMsgValue || "Sync failed")
        this.enableAllSyncButtons()
        this.setButtonLoading(button, false)
      }
    } catch (error) {
      console.error("Sync error:", error)
      this.showToast("error", this.syncErrorMsgValue || "An error occurred during sync")
      this.enableAllSyncButtons()
      this.setButtonLoading(button, false)
    }
  }

  /**
   * Set button loading state
   */
  setButtonLoading(button, loading) {
    if (!button) return

    if (loading) {
      button.disabled = true
      button.dataset.originalHtml = button.innerHTML
      const icon = button.querySelector(".material-symbols-outlined")
      if (icon) {
        icon.textContent = "sync"
        icon.classList.add("spin-animation")
      }
    } else {
      button.disabled = false
      if (button.dataset.originalHtml) {
        button.innerHTML = button.dataset.originalHtml
        delete button.dataset.originalHtml
      }
    }
  }

  /**
   * Disable all sync buttons
   */
  disableAllSyncButtons() {
    if (this.hasSyncButtonTarget) this.syncButtonTarget.disabled = true
    if (this.hasSyncFullButtonTarget) this.syncFullButtonTarget.disabled = true
  }

  /**
   * Enable all sync buttons
   */
  enableAllSyncButtons() {
    if (this.hasSyncButtonTarget) this.syncButtonTarget.disabled = false
    if (this.hasSyncFullButtonTarget) this.syncFullButtonTarget.disabled = false
  }

  /**
   * Poll for sync completion, then reload.
   * Fallback for when Turbo Stream broadcast doesn't reach the client
   * (e.g. WebSocket disconnected). Primary updates come via Turbo Streams.
   */
  #pollSyncStatus(attempt = 0) {
    const maxAttempts = 10
    const delay = 2000

    // A status fetch that resolves after the member left must not re-arm a
    // timer disconnect() has already cleared.
    if (!this.element.isConnected) return

    if (attempt >= maxAttempts) {
      this.#reload()
      return
    }

    this.reloadTimer = setTimeout(async () => {
      if (!this.hasConnectionStatusUrlValue) {
        this.#reload()
        return
      }

      try {
        const response = await fetch(this.connectionStatusUrlValue, {
          headers: { Accept: "application/json" }
        })

        if (response.ok) {
          const data = await response.json()
          if (data.last_sync_status === "success" || data.last_sync_status === "error") {
            this.#reload()
            return
          }
        }
      } catch {
        // Ignore fetch errors, continue polling
      }

      this.#pollSyncStatus(attempt + 1)
    }, delay)
  }

  // Waits while the member's own click is in flight, and never refreshes the
  // page that replaced this one: window.location is already that page's URL.
  #reload() {
    if (!this.element.isConnected) return

    this.cancelReload = refreshWhenIdle(window.location.href)
  }

  /**
   * Show toast notification using the existing toast system
   */
  showToast(type, message) {
    const container = document.getElementById("toast_container")
    if (!container) return

    const iconMap = { success: "check_circle", error: "error", warning: "warning", info: "info" }
    const colorMap = { success: "text-success", error: "text-danger", warning: "text-warning", info: "text-info" }
    const icon = iconMap[type] || "info"
    const colorClass = colorMap[type] || "text-info"

    const toast = document.createElement("div")
    toast.className = "toast"
    toast.setAttribute("role", "alert")
    toast.setAttribute("aria-live", "assertive")
    toast.setAttribute("aria-atomic", "true")
    toast.dataset.controller = "toast"

    const header = document.createElement("div")
    header.className = "toast-header text-white"

    const iconSpan = document.createElement("span")
    iconSpan.className = `material-symbols-outlined ${colorClass}`
    iconSpan.setAttribute("aria-hidden", "true")
    iconSpan.textContent = icon

    const strong = document.createElement("strong")
    strong.className = "me-auto"
    strong.textContent = message

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.className = "btn-close btn-close-white"
    closeBtn.setAttribute("data-bs-dismiss", "toast")
    closeBtn.setAttribute("aria-label", "Close")

    header.append(iconSpan, strong, closeBtn)
    toast.appendChild(header)
    container.appendChild(toast)
  }
}
